import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Clock, BadgeCheck, PlaneLanding, ShieldCheck, ArrowRight, MapPin, Star } from "lucide-react";
import { useI18n } from "../i18n";
import { useBooking } from "../lib/booking-context";
import { useCurrency } from "../lib/currency-context";
import { HeroScene } from "./HeroScene";
import { Magnetic } from "./Magnetic";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const { t } = useI18n();
  const { requestBooking } = useBooking();
  const { fmt, currency } = useCurrency();
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");
  const reduce = useReducedMotion();

  // mouse parallax for the scene
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const spx = useSpring(px, { stiffness: 120, damping: 20 });
  const spy = useSpring(py, { stiffness: 120, damping: 20 });
  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set(((e.clientX - (r.left + r.width / 2)) / r.width) * 24);
    py.set(((e.clientY - (r.top + r.height / 2)) / r.height) * 24);
  };
  const onLeave = () => { px.set(0); py.set(0); };

  const trust = [
    { icon: Clock, label: t("hero.badge1") },
    { icon: BadgeCheck, label: t("hero.badge2") },
    { icon: PlaneLanding, label: t("hero.badge3") },
    { icon: ShieldCheck, label: t("hero.badge4") },
  ];
  const stats = t("stats");

  return (
    <section id="top" className="relative overflow-hidden hero-decor pt-28 pb-14 lg:pt-32 lg:pb-20">
      <div className="absolute inset-0 grid-bg opacity-70" />
      <div className="mesh-glow -top-32 right-[-6rem]" />
      <div className="relative max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.08 } } }} className="lg:col-span-6">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1">
              <Star className="h-3.5 w-3.5 text-[hsl(var(--lime))] fill-[hsl(var(--lime))]" />
              <span className="text-xs font-medium text-muted-foreground">{t("hero.eyebrow")}</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="mt-5 font-display font-semibold tracking-[-0.02em] text-4xl sm:text-5xl lg:text-[4rem] leading-[1.02] text-foreground">
              {t("hero.title")}
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              {t("hero.subtitle")}
            </motion.p>

            <motion.div variants={fadeUp} className="mt-7 flex flex-wrap items-center gap-3">
              <Magnetic strength={0.45}>
                <Button size="lg" className="rounded-full shimmer cta-glow active:scale-[0.98] h-13 px-7 text-base h-12" onClick={() => requestBooking({})} data-testid="hero-book-button">
                  {t("cta.book")} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Magnetic>
              <Button size="lg" variant="outline" className="rounded-full border-border bg-card/40 h-12 px-6 active:scale-[0.98] hover:border-[hsl(82_100%_55%/0.5)]" onClick={() => document.getElementById("routes")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-prices-button">
                {t("cta.prices")}
              </Button>
            </motion.div>

            <motion.ul variants={fadeUp} className="mt-9 grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
              {trust.map((b, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(82_100%_55%/0.14)]">
                    <b.icon className="h-4 w-4 text-[hsl(var(--lime))]" />
                  </span>
                  <span className="font-medium">{b.label}</span>
                </li>
              ))}
            </motion.ul>
          </motion.div>

          <div className="lg:col-span-6" onMouseMove={onMove} onMouseLeave={onLeave}>
            <motion.div style={{ x: spx, y: spy }} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <HeroScene />
            </motion.div>

            <div className="relative -mt-6 mx-3 rounded-[18px] border border-border glass flat-card p-4 sm:p-5" data-testid="hero-booking-card">
              <div className="flex items-center justify-between">
                <p className="font-display text-base font-semibold">{t("hero.quickTitle")}</p>
                <span className="inline-flex items-center rounded-full bg-[hsl(82_100%_55%/0.16)] text-[hsl(var(--lime))] px-2.5 py-0.5 text-[11px] font-semibold tabular-nums">{fmt(25, currency === "EUR" ? 2 : 0)}/km</span>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder={t("calc.from")} className="pl-9 rounded-xl h-11 focus-gold bg-background/60" data-testid="hero-from-input" />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={t("calc.to")} className="pl-9 rounded-xl h-11 focus-gold bg-background/60" data-testid="hero-to-input" />
                </div>
              </div>
              <Button className="mt-3 w-full rounded-xl shimmer active:scale-[0.98] h-11" onClick={() => requestBooking({ from_location: from, to_location: to })} data-testid="hero-quick-book-button">
                {t("cta.book")}
              </Button>
            </div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="mt-12 rounded-2xl border border-border glass overflow-hidden" data-testid="trust-strip">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border">
            {Array.isArray(stats) && stats.map((s, i) => (
              <div key={i} className="px-5 py-5">
                <p className="font-display text-xl sm:text-2xl font-semibold lime-text tabular-nums">{s.v}</p>
                <p className="mt-1 text-xs text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
